import { useEffect, useRef, useState } from "react";

const INTERACTIVE_TAGS = ["A", "BUTTON", "INPUT", "TEXTAREA", "SELECT", "LABEL"];

const isInteractive = (n) => {
  if (!n || !n.tagName) return false;
  if (INTERACTIVE_TAGS.includes(n.tagName)) return true;
  return !!(n.getAttribute && (n.getAttribute("role") === "button" || n.dataset?.hover === "true"));
};

/** Toggles the grown cursor state when hovering interactive elements. */
const makeHoverHandler = (el) => (e) => {
  if (!el) return;
  const t = e.target;
  if (isInteractive(t) || t.closest?.("a, button, [data-hover='true']")) {
    el.classList.add("is-hover");
  } else {
    el.classList.remove("is-hover");
  }
};

/** Starts the eased follow loop; returns a stop function. */
const startFollowLoop = (el, getTarget) => {
  let raf;
  let cx = 0, cy = 0;
  const loop = () => {
    const { x, y } = getTarget();
    cx += (x - cx) * 0.22;
    cy += (y - cy) * 0.22;
    if (el) el.style.transform = `translate(${cx}px, ${cy}px) translate(-50%, -50%)`;
    raf = requestAnimationFrame(loop);
  };
  raf = requestAnimationFrame(loop);
  return () => cancelAnimationFrame(raf);
};

/** Sticky desktop cursor: mix-blend-difference dot that grows over interactive items. */
export default function Cursor() {
  const ref = useRef(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    // Only on devices with fine pointer
    const m = window.matchMedia("(hover: hover) and (pointer: fine)");
    setEnabled(m.matches);
    const handler = (e) => setEnabled(e.matches);
    m.addEventListener("change", handler);
    return () => m.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    const mouse = { x: 0, y: 0 };

    const onMove = (e) => { mouse.x = e.clientX; mouse.y = e.clientY; };
    const onOver = makeHoverHandler(el);
    const stopLoop = startFollowLoop(el, () => mouse);

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseover", onOver);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseover", onOver);
      stopLoop();
    };
  }, [enabled]);

  if (!enabled) return null;
  return <div ref={ref} className="sol-cursor" data-testid="sol-cursor" />;
}
